import { create } from "zustand";
import {
  AdminAgencyApplicationListItem,
  AdminAgencyApplicationResponse,
  AdminAgencyApplicationsResponse,
  ApplicationNote,
} from "./types";
import { toast } from "react-toastify";
import { env } from "@/lib/env";
import { ApiError } from "@/types/types";
import { sleep } from "@/lib/sleep";
import { apiFetch } from "@/lib/apiFetch";

const ADMINENPOINTURL = env.NEXT_ADMIN_API_URL as string;

type ApplicationsStore = {
  applications: AdminAgencyApplicationListItem[];
  page_number: number;
  total_count: number;
  number_of_pages: number;
  isLoading: boolean;
  isApplicationModalOpen: boolean;
  currentApplication: AdminAgencyApplicationResponse | null;
  isApplicationLoading: boolean;
  query: string;
  status: "pending" | "approved" | "rejected" | null;
  openApplication: (id: string, idToken: string | null) => Promise<void>;
  closeApplication: () => void;
  initState: (data: AdminAgencyApplicationsResponse) => void;
  fetchPage: (idToken: string | null, page: number) => Promise<void>;
  setQuery: (q: string) => void;
  setStatus: (s: ApplicationsStore["status"]) => void;
  sendMessage: (
    applicatioId: string,
    message: string,
    idToken: string | null,
    userName: string,
    userPhotoUrl: string,
    authorSub: string,
  ) => Promise<ApplicationNote | null>;
  updateApplicationStatus: (
    requestId: string,
    newStatus: "approved" | "rejected",
    message: string,
    idToken: string | null,
    adminSub: string,
    adminPhotoUrl: string,
  ) => Promise<boolean>;
};

export const useApplicationStore = create<ApplicationsStore>((set, get) => ({
  applications: [],
  page_number: 1,
  total_count: 0,
  number_of_pages: 1,
  isLoading: true,
  isApplicationModalOpen: false,
  currentApplication: null,
  isApplicationLoading: false,
  query: "",
  status: null,
  openApplication: async (id, idToken) => {
    set({
      isApplicationModalOpen: true,
      isApplicationLoading: true,
      currentApplication: null,
    });

    try {
      const res = await apiFetch(`/api/admin/getApplication?request_id=${id}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const apiErr = (await res.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      const data = (await res.json()) as AdminAgencyApplicationResponse;

      set({
        currentApplication: data,
        isApplicationLoading: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load application.";

      if (errorMessage !== "UNAUTHORIZED") {
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to send message."
            : errorMessage,
          {
            type: "error",
          },
        );
      }

      set({
        isApplicationModalOpen: false,
        isApplicationLoading: false,
        currentApplication: null,
      });
    }
  },
  closeApplication: () => {
    set({
      isApplicationModalOpen: false,
      currentApplication: null,
      isApplicationLoading: false,
    });
  },
  initState: (data) => {
    set({
      applications: data.items,
      page_number: data.page_number,
      total_count: data.total_count,
      number_of_pages: data.number_of_pages,
      isLoading: false,
    });
  },
  sendMessage: async (
    applicationId,
    message,
    idToken,
    authorName,
    authorPhotoUrl,
    authorSub,
  ) => {
    try {
      const response = await apiFetch(`/api/admin/sendApplicationMessage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: applicationId,
          message: message,
        }),
      });
      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      const newComment = <ApplicationNote>{
        author_name: authorName,
        author_photo_url: authorPhotoUrl,
        author_sub: authorSub,
        message,
        created_at: new Date().toISOString(),
      };
      set((state) => ({
        currentApplication:
          state.currentApplication &&
          state.currentApplication.request_id === applicationId
            ? {
                ...state.currentApplication,
                notes: [...state.currentApplication.notes, newComment],
              }
            : state.currentApplication,
      }));
      return newComment;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      if (errorMessage !== "UNAUTHORIZED") {
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to send message."
            : errorMessage,
          {
            type: "error",
          },
        );
      }
      return null;
    }
  },
  updateApplicationStatus: async (
    requestId,
    newStatus,
    message,
    idToken,
    adminSub,
    adminPhotoUrl,
  ) => {
    try {
      const response = await apiFetch(
        `/api/admin/updateAgencyApplicationStatus`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            admin_sub: adminSub,
            request_id: requestId,
            new_status: newStatus,
            message,
          }),
        },
      );

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      const now = new Date().toISOString();

      set((state) => ({
        // update list item
        applications: state.applications.map((app) =>
          app.request_id === requestId ? { ...app, status: newStatus } : app,
        ),

        // update currently opened application
        currentApplication:
          state.currentApplication &&
          state.currentApplication.request_id === requestId
            ? {
                ...state.currentApplication,
                status: newStatus,
                reviewed_at: now,
                reviewed_by: adminSub,
                reviewed_by_name:
                  state.currentApplication.reviewed_by_name ?? null,
                reviewed_by_photo_url: adminPhotoUrl,
              }
            : state.currentApplication,
      }));

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";

      if (errorMessage !== "UNAUTHORIZED") {
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to send message."
            : errorMessage,
          {
            type: "error",
          },
        );
      }

      return false;
    }
  },
  fetchPage: async (idToken, page) => {
    const { query, status, page_number } = get();
    const targetPage = page ?? page_number;

    set({ isLoading: true, page_number: targetPage });

    try {
      const params = new URLSearchParams();
      params.set("page", String(targetPage));

      if (query) params.set("q", query);
      if (status) params.set("status", status);

      const res = await apiFetch(
        `/api/admin/getApplications?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        const apiErr = (await res.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      const data = (await res.json()) as AdminAgencyApplicationsResponse;

      set({
        applications: data.items,
        page_number: data.page_number,
        total_count: data.total_count,
        number_of_pages: data.number_of_pages,
        isLoading: false,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load applications.";

      if (errorMessage !== "UNAUTHORIZED") {
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to send message."
            : errorMessage,
          {
            type: "error",
          },
        );
      }

      set({ isLoading: false });
    }
  },
  setQuery: (q) => {
    set({
      query: q,
    });
  },

  setStatus: (s) => {
    set({
      status: s,
    });
  },
}));
